import HeartButton from "./heart/HeartButton";
import ShopButton from "./shop/ShopButton";
import ShareButton from "./ShareButton";
import ShareModal from "./ShareModal";
import { PlayerMeta, ProductModel, PusherConfig } from "@/types";
import { MdFullscreen, MdFullscreenExit, MdTranslate } from "react-icons/md";
import { IoChatbubbleEllipses } from "react-icons/io5";
import Pusher from "pusher-js";
import { FiSend } from "react-icons/fi";
import { useState } from "react";
import i18n from "@/i18n";

export interface SideMenuProps {
  id: string;
  products: ProductModel[];
  fullScreen: boolean;
  hideFullScreen: boolean;
  onEnterFullScreen: () => void;
  onExitFullScreen: () => void;
  meta: PlayerMeta;
  onCommentSend: (
    nickname: string,
    message: string,
    meta: PlayerMeta
  ) => Promise<boolean | void>;
  onHeartSend: (meta: PlayerMeta) => Promise<void>;
  pusher: Pusher | null;
  pusherConfig: PusherConfig | null;
  showChat: boolean;
  onTapChatMessageIcon: (status: boolean) => void;
  onTapChatInputIcon: () => void;
  onTapShopIcon: () => void;
  hideShopIcon?: boolean;
  landscape?: boolean;
  isMobile?: boolean;
  onLanguageMenuChange?: (isOpen: boolean) => void;
  onShareModalChange?: (isOpen: boolean) => void;
  currentUrl?: string;
  userId?: string;
  hideSideMenu?: boolean;
}

const SideMenu = ({
  id,
  products = [],
  fullScreen,
  hideFullScreen,
  onEnterFullScreen,
  onExitFullScreen,
  meta,
  pusher,
  pusherConfig,
  onHeartSend,
  showChat,
  onTapChatMessageIcon,
  onTapChatInputIcon,
  onTapShopIcon,
  hideShopIcon = false,
  landscape = false,
  isMobile = false,
  onLanguageMenuChange,
  onShareModalChange,
  currentUrl = window.location.href,
  userId = "anonymous",
  hideSideMenu = false,
}: SideMenuProps) => {
  const [showLanguageMenu, setShowLanguageMenu] = useState<boolean>(false);
  const [showShareModal, setShowShareModal] = useState<boolean>(false);

  const isLandscapeMode = !!landscape;
  const isMobileMini = isMobile && !fullScreen;
  const largeButtonClass = "tw-p-2 tw-w-[32px] tw-h-[32px]";
  const mediumButtonClass = "tw-p-1.5 tw-w-[26px] tw-h-[26px]";
  const smallButtonClass = "tw-p-1.5 tw-w-[24px] tw-h-[24px]";
  const compactButtonClass = "tw-p-1 tw-w-[22px] tw-h-[22px]";
  const mobileButtonClass = "tw-p-1 tw-w-[20px] tw-h-[20px] tw-m-0";
  const primaryButtonClass =
    isMobileMini
      ? mobileButtonClass
      : !fullScreen && isLandscapeMode
        ? compactButtonClass
        : fullScreen
          ? largeButtonClass
          : mediumButtonClass;
  const secondaryButtonClass =
    isMobileMini
      ? mobileButtonClass
      : !fullScreen && isLandscapeMode
        ? compactButtonClass
        : fullScreen
          ? largeButtonClass
          : smallButtonClass;
  const iconSize = isMobileMini ? "0.85rem" : isLandscapeMode ? "1rem" : fullScreen ? "1.4rem" : "1.2rem";
  const spacingClass = isMobileMini
    ? "tw-space-y-1.5 tw-top-[55%]"
    : isLandscapeMode
      ? "tw-space-y-2 tw-top-[50%]"
      : fullScreen
        ? "tw-space-y-4 tw-top-[70%]"
        : "tw-space-y-3 tw-top-[60%]";
  const containerPaddingClass =
    isMobileMini
      ? "tw-p-1"
      : !fullScreen && isLandscapeMode
        ? "tw-p-1.5"
        : fullScreen
          ? "tw-p-2"
          : "tw-p-2.5";

  const languages = [
    { code: "ja", label: "日本語" },
    { code: "en", label: "English" },
    { code: "zh", label: "中文" },
  ];

  const handleLanguageChange = async (langCode: string) => {
    try {
      await i18n.changeLanguage(langCode);
      setShowLanguageMenu(false);
      onLanguageMenuChange?.(false);
    } catch (error) {
      console.error("Failed to change language:", error);
    }
  };

  const toggleLanguageMenu = () => {
    const newState = !showLanguageMenu;
    setShowLanguageMenu(newState);
    onLanguageMenuChange?.(newState);
  };

  const handleOpenShareModal = () => {
    setShowShareModal(true);
    onShareModalChange?.(true);
  };

  const handleCloseShareModal = (status: boolean) => {
    setShowShareModal(status);
    onShareModalChange?.(status);
  };

  if (hideSideMenu) {
    return null;
  }

  return (
    <>
      {!showShareModal && (
        <div
          className={`
            tw-absolute tw-right-2 tw-transform tw--translate-y-1/2 tw-bg-black-700 tw-opacity-80
            tw-flex tw-flex-col tw-justify-center tw-items-center
            ${containerPaddingClass} tw-rounded-lg tw-z-[100]
            ${spacingClass}
          `}
        >
          <div
            className={`tw-cursor-pointer tw-bg-gray-500/50 tw-rounded-full tw-flex tw-items-center tw-justify-center ${primaryButtonClass}`}
          >
            <HeartButton
              pusher={pusher}
              pusherConfig={pusherConfig}
              meta={meta}
              onHeartSend={onHeartSend}
            />
          </div>
          <div
            className={`tw-cursor-pointer tw-bg-gray-500/50  tw-rounded-full tw-flex tw-items-center tw-justify-center ${primaryButtonClass}`}
          >
            <button
              className="tw-text-sky-500 tw-bg-transparent tw-border-none tw-outline-none tw-shadow-none hover:tw-cursor-pointer"
              onClick={onTapChatInputIcon}
            >
              <IoChatbubbleEllipses size={iconSize} />
            </button>
          </div>
          {!hideShopIcon && (
            <div
              className={`tw-cursor-pointer tw-bg-gray-500/50 tw-rounded-full tw-flex tw-items-center tw-justify-center ${secondaryButtonClass}`}
            >
              <ShopButton
                id={id}
                products={products}
                fullscreen={fullScreen}
                onTapShopIcon={onTapShopIcon}
              ></ShopButton>
            </div>
          )}
          <div
            className={`tw-cursor-pointer tw-bg-gray-500/50 tw-rounded-full tw-flex tw-items-center tw-justify-center ${secondaryButtonClass}`}
          >
            <ShareButton
              onClick={handleOpenShareModal}
              url={currentUrl}
              title={meta?.title || ""}
            />
          </div>
          <div className="tw-relative">
            <div
              onClick={toggleLanguageMenu}
              className={`tw-cursor-pointer tw-bg-gray-500/50 tw-rounded-full tw-flex tw-items-center tw-justify-center tw-border-none tw-outline-none ${secondaryButtonClass}`}
            >
              <MdTranslate className="tw-text-white" size={iconSize} />
            </div>
            {showLanguageMenu && (
              <div className="tw-absolute tw-right-full tw-mr-2 tw-top-0 tw-py-2 tw-w-32 tw-bg-white/70 tw-backdrop-blur-sm tw-rounded-lg tw-shadow-xl">
                {languages.map((lang) => (
                  <button
                    key={lang.code}
                    onClick={() => handleLanguageChange(lang.code)}
                    className="tw-block tw-w-full tw-text-left tw-px-4 tw-py-2 tw-text-sm tw-text-black hover:tw-bg-gray-200/80 tw-border-none tw-outline-none tw-cursor-pointer"
                  >
                    {lang.label}
                  </button>
                ))}
              </div>
            )}
          </div>
          {!hideFullScreen && (
            <div
              className={`tw-cursor-pointer tw-bg-gray-500/50 tw-rounded-full tw-flex tw-items-center tw-justify-center ${secondaryButtonClass}`}
              onClick={fullScreen ? onExitFullScreen : onEnterFullScreen}
            >
              {fullScreen ? (
                <MdFullscreenExit className="tw-text-white" size={iconSize} />
              ) : (
                <MdFullscreen className="tw-text-white" size={iconSize} />
              )}
            </div>
          )}
        </div>
      )}

      <ShareModal
        open={showShareModal}
        onClose={handleCloseShareModal}
        url={currentUrl}
        title={meta?.title || ""}
        programId={meta?.id || id}
        userId={userId}
        fullScreen={fullScreen}
      />
    </>
  );
};

export default SideMenu;
